<script lang="ts">
export interface SelectOption {
  [key: string]: unknown;
}
</script>

<script setup lang="ts">
import type { PropType, Ref } from 'vue';

import { onClickOutside, useEventListener, useVirtualList } from '@vueuse/core';
import { computed, nextTick, ref, useAttrs, useId, useSlots, watch } from 'vue';

import { useFloatingPosition } from '@/shared/lib/composables';
import { moveFocusFromTrigger } from '@/shared/lib/helpers';
import { IconChevronDown, IconCross } from '@/shared/ui/icons';
import { OverlayTeleport } from '@/shared/ui/overlay';

import AppSelectOption from './AppSelectOption.vue';

defineOptions({
  inheritAttrs: false,
});

type SelectedValue = number | string | (number | string)[] | null;
type VariantType = 'none' | 'outline';
type SizeType = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const props = defineProps({
  label: {
    type: String,
    default: undefined,
  },
  modelValue: {
    type: [Number, String, Array] as PropType<SelectedValue>,
    default: null,
  },
  open: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Array as PropType<SelectOption[]>,
    default: () => [],
  },
  optionValue: {
    type: String,
    default: 'value',
  },
  optionLabel: {
    type: String,
    default: 'label',
  },
  placeholder: {
    type: String,
    default: 'Не выбрано',
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  // Для длинных каталогов с однострочными вариантами фиксированной высоты.
  virtual: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String as PropType<VariantType>,
    default: 'outline',
    validator: (value: string) => ['none', 'outline'].includes(value),
  },
  size: {
    type: String as PropType<SizeType>,
    default: 'md',
    validator: (value: string) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(value),
  },
  variantIsError: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:modelValue': [value: SelectedValue];
  'update:open': [value: boolean];
  change: [value: SelectedValue];
}>();

const attrs = useAttrs();
const slots = useSlots();
const generatedSelectId = useId();
const triggerId = computed(() => {
  const providedId = attrs.id;

  return typeof providedId === 'string' && providedId.trim() !== '' ? providedId : generatedSelectId;
});
const labelId = computed(() => `${triggerId.value}-label`);
const listboxId = computed(() => `${triggerId.value}-listbox`);
const hasLabel = computed(() => Boolean(props.label || slots.label));
const triggerLabelledBy = computed(() => {
  if (hasLabel.value) {
    return `${labelId.value} ${triggerId.value}`;
  }

  const providedLabelledBy = attrs['aria-labelledby'];

  return typeof providedLabelledBy === 'string' ? providedLabelledBy : undefined;
});
const triggerAttrs = computed<Record<string, unknown>>(() =>
  Object.fromEntries(
    Object.entries(attrs).filter(
      ([key]) =>
        ![
          'aria-activedescendant',
          'aria-controls',
          'aria-expanded',
          'aria-haspopup',
          'aria-labelledby',
          'class',
          'id',
          'style',
        ].includes(key),
    ),
  ),
);

// Как у AppInput: ошибку ставит форма при отправке, поле встряхивается, чтобы его было видно.
const isShaking = ref(false);
watch(
  () => props.variantIsError,
  (hasError) => {
    if (hasError) isShaking.value = true;
  },
);

const isOpen = ref(props.open);
const activeOptionIndex = ref(-1);
const search = ref('');
const searchInput = ref<HTMLInputElement | null>(null);
const container = ref<HTMLElement | null>(null);

// Синхронизация с внешним состоянием open
watch(
  () => props.open,
  (newValue) => {
    isOpen.value = newValue;
  },
);

const selected = ref<SelectedValue>(props.multiple ? [] : null);

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.multiple && Array.isArray(newValue)) {
      selected.value = newValue;
    } else if (props.multiple && newValue) {
      if (Array.isArray(selected.value)) {
        selected.value.push(newValue as number | string);
      }
    } else {
      selected.value = newValue;
    }
  },
  { immediate: true },
);

const getOptionValue = (option: SelectOption): string | number | null => {
  const value = option[props.optionValue];
  // Поддержка явного null в опциях
  if (value === null) return null;
  return typeof value === 'string' || typeof value === 'number' ? value : null;
};

const getOptionLabel = (option: SelectOption): string => {
  const label = option[props.optionLabel];
  return typeof label === 'string' || typeof label === 'number' ? String(label) : '';
};

const hasSelected = computed(() => {
  if (props.multiple) {
    return Array.isArray(selected.value) && selected.value.length > 0;
  } else {
    if (selected.value === null) {
      return props.options.some((option) => option[props.optionValue] === null);
    }
    return selected.value !== '';
  }
});

const isValueSelected = (value: number | string | null) => {
  if (!hasSelected.value) {
    return false;
  }

  if (props.multiple && Array.isArray(selected.value)) {
    // В multiple режиме null не поддерживается
    if (value === null) return false;
    return selected.value.indexOf(value) !== -1;
  } else {
    // Поддержка сравнения с null
    return selected.value === value;
  }
};

const getOption = (value: number | string | null) => {
  // Поддержка поиска опции с null значением
  return props.options.find((opt) => getOptionValue(opt) === value);
};

const getSelectedLabel = computed(() => {
  if (props.multiple && Array.isArray(selected.value)) {
    return selected.value
      .map((value) => {
        const option = getOption(value);
        return option ? getOptionLabel(option) : '';
      })
      .filter(Boolean)
      .join(', ');
  }

  const option = getOption(selected.value as number | string | null);
  return option ? getOptionLabel(option) : '';
});

const emitSelectedValue = () => {
  emit('update:modelValue', selected.value);
  emit('change', selected.value);
};

const select = (value: number | string | null) => {
  if (props.disabled) {
    return;
  }

  if (props.multiple && Array.isArray(selected.value) && !isValueSelected(value)) {
    // В multiple режиме null не поддерживается
    if (value !== null) {
      selected.value.push(value);
    }
  }

  if (!props.multiple) {
    selected.value = value;
  }

  emitSelectedValue();

  if (!props.multiple) {
    void nextTick(() => {
      isOpen.value = false;
    });
  }
};

const unselect = (value: number | string | null) => {
  if (props.multiple && Array.isArray(selected.value)) {
    selected.value = selected.value.filter((selectedValue) => selectedValue !== value);
  } else {
    selected.value = null;
  }

  emitSelectedValue();

  if (!props.multiple) {
    isOpen.value = false;
  }
};

const toggle = (value: number | string | null) => {
  if (isValueSelected(value)) {
    // Если clearable=false, не снимаем выбранное значение
    if (props.clearable) {
      unselect(value);
    }
  } else {
    select(value);
  }
};

const filteredOptions = computed(() => {
  const query = search.value.trim().toLocaleLowerCase('ru');
  if (!props.searchable || !query) return props.options;
  return props.options.filter((option) =>
    `${getOptionLabel(option)} ${getOptionValue(option) ?? ''}`.toLocaleLowerCase('ru').includes(query),
  );
});

const renderedOptions = computed(() => (isOpen.value ? filteredOptions.value : []));
const virtualized = computed(() => props.virtual && renderedOptions.value.length > 50);
const optionHeight = computed(() => ({ xs: 28, sm: 36, md: 44, lg: 52, xl: 60 })[props.size]);
const optionStride = computed(() => optionHeight.value + 4);
const {
  list: virtualOptions,
  containerProps,
  wrapperProps,
} = useVirtualList(
  computed(() => (virtualized.value ? renderedOptions.value : [])),
  { itemHeight: () => optionStride.value, overscan: 3 },
);
const listbox = containerProps.ref;
const visibleOptions = computed(() =>
  virtualized.value ? virtualOptions.value : renderedOptions.value.map((option, index) => ({ data: option, index })),
);

// Позиционирование: floating-ui
const {
  reference,
  floating: dropdown,
  dropdownStyles: dropdownStyle,
  isPositioned,
} = useFloatingPosition({
  open: isOpen,
  placement: 'bottom-start',
  widthBehaviour: 'match',
  maxHeight: 360,
});

const trigger = reference as Ref<HTMLButtonElement | null>;

const optionId = (index: number) => `${listboxId.value}-option-${index}`;

const scrollActiveOptionIntoView = () => {
  if (activeOptionIndex.value < 0) {
    return;
  }

  void nextTick(() => {
    const element = listbox.value;
    if (!isOpen.value || !element || activeOptionIndex.value < 0) return;

    let top: number;
    let height: number;
    if (virtualized.value) {
      top = activeOptionIndex.value * optionStride.value;
      height = optionHeight.value;
    } else {
      const option = document.getElementById(optionId(activeOptionIndex.value));
      if (!option) return;
      top = option.getBoundingClientRect().top - element.getBoundingClientRect().top + element.scrollTop;
      height = option.offsetHeight;
    }

    // Меняем только внутреннюю прокрутку: форма и страница остаются на месте.
    if (top < element.scrollTop) element.scrollTop = top;
    else if (top + height > element.scrollTop + element.clientHeight) {
      element.scrollTop = top + height - element.clientHeight;
    }
    containerProps.onScroll();
  });
};

const setActiveOption = (index: number, scroll = true) => {
  if (filteredOptions.value.length === 0) {
    activeOptionIndex.value = -1;
    return;
  }

  activeOptionIndex.value = Math.min(Math.max(index, 0), filteredOptions.value.length - 1);
  if (scroll) scrollActiveOptionIntoView();
};

const selectedOptionIndex = () => filteredOptions.value.findIndex((option) => isValueSelected(getOptionValue(option)));

const initializeActiveOption = (fallback: 'first' | 'last' = 'first') => {
  const selectedIndex = selectedOptionIndex();

  if (selectedIndex >= 0) {
    setActiveOption(selectedIndex);
    return;
  }

  setActiveOption(fallback === 'last' ? filteredOptions.value.length - 1 : 0);
};

watch(
  isOpen,
  (newValue, oldValue) => {
    if (oldValue !== undefined) emit('update:open', newValue);
    if (newValue && activeOptionIndex.value < 0) initializeActiveOption();
    if (!newValue) {
      search.value = '';
      activeOptionIndex.value = -1;
    }
  },
  { immediate: true },
);

watch([isOpen, isPositioned], ([open, positioned]) => {
  if (open && positioned) {
    scrollActiveOptionIntoView();
    if (props.searchable) void nextTick(() => searchInput.value?.focus());
  }
});

const focusSearchAfterEnter = () => {
  if (isOpen.value && props.searchable && document.activeElement === trigger.value) searchInput.value?.focus();
};

const clearSearch = () => {
  search.value = '';
  searchInput.value?.focus();
};

watch(
  () => filteredOptions.value.map(getOptionValue),
  (values, previousValues) => {
    const sameValues =
      values.length === previousValues.length && values.every((value, i) => value === previousValues[i]);
    if (isOpen.value && !sameValues) initializeActiveOption();
  },
);

const openDropdown = (fallback: 'first' | 'last' = 'first') => {
  if (props.disabled) {
    return;
  }

  isOpen.value = true;
  initializeActiveOption(fallback);
};

const closeDropdown = (returnFocus = false) => {
  isOpen.value = false;

  if (returnFocus) {
    void nextTick(() => trigger.value?.focus());
  }
};

const toggleDropdown = () => {
  if (props.disabled) {
    return;
  }

  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

const moveActiveOption = (direction: 1 | -1) => {
  if (!isOpen.value) {
    openDropdown(direction === 1 ? 'first' : 'last');
    return;
  }

  if (activeOptionIndex.value < 0) {
    setActiveOption(direction === 1 ? 0 : filteredOptions.value.length - 1);
    return;
  }

  setActiveOption(activeOptionIndex.value + direction);
};

const selectActiveOption = () => {
  const option = filteredOptions.value[activeOptionIndex.value];

  if (!option) {
    return;
  }

  selectOption(option);
};

const selectOption = (option: SelectOption) => {
  toggle(getOptionValue(option));
  if (!props.multiple) closeDropdown(true);
};

const handleTriggerKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'Enter':
      event.preventDefault();
      if (isOpen.value) {
        selectActiveOption();
      } else {
        openDropdown();
      }
      break;
    case ' ':
      event.preventDefault();
      openDropdown();
      break;
    case 'ArrowDown':
      event.preventDefault();
      moveActiveOption(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      moveActiveOption(-1);
      break;
    case 'Home':
      event.preventDefault();
      openDropdown();
      setActiveOption(0);
      break;
    case 'End':
      event.preventDefault();
      openDropdown('last');
      setActiveOption(filteredOptions.value.length - 1);
      break;
    case 'Escape':
      if (isOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        closeDropdown(true);
      }
      break;
    case 'Tab':
      closeDropdown();
      break;
    default:
      // Печать на кнопке начинает поиск: символы до фокуса поля поиска не теряются.
      if (props.searchable && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        search.value += event.key;
        openDropdown();
      }
  }
};

const handleSearchKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    event.preventDefault();
    closeDropdown();
    void nextTick(() => moveFocusFromTrigger(trigger.value, event.shiftKey));
  } else if (['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(event.key)) {
    handleTriggerKeydown(event);
  }
};

onClickOutside(
  container,
  () => {
    closeDropdown();
  },
  { ignore: [dropdown] },
);

// Обработчик клавиатуры для закрытия по Escape
useEventListener(document, 'keydown', (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    event.preventDefault();
    closeDropdown(true);
  }
});

const activeDescendant = computed(() =>
  isOpen.value && visibleOptions.value.some((option) => option.index === activeOptionIndex.value)
    ? optionId(activeOptionIndex.value)
    : undefined,
);

// Computed классы для размеров и вариантов
const selectClasses = computed(() => {
  const baseClasses = 'select-display';
  const sizeClass = `select-size-${props.size}`;
  const variantClass = `select-variant-${props.variant}`;
  const disabledClass = props.disabled ? 'select-disabled' : '';

  return [baseClasses, sizeClass, variantClass, disabledClass].filter(Boolean).join(' ');
});

const optionClasses = computed(() => {
  const sizeClasses = {
    xs: 'select-option-size-xs',
    sm: 'select-option-size-sm',
    md: 'select-option-size-md',
    lg: 'select-option-size-lg',
    xl: 'select-option-size-xl',
  };

  return sizeClasses[props.size];
});
</script>

<template>
  <div
    ref="container"
    class="relative flex flex-col gap-1.5 select-none"
    :class="$attrs.class"
    :style="$attrs.style"
  >
    <label
      v-if="hasLabel"
      :id="labelId"
      :for="triggerId"
      class="text-sm font-medium text-(--text-primary)"
    >
      <slot name="label">{{ label }}</slot>
    </label>

    <button
      :id="triggerId"
      ref="trigger"
      v-bind="triggerAttrs"
      type="button"
      :class="[selectClasses, { 'border-(--danger)': props.variantIsError, 'motion-safe:animate-shake': isShaking }]"
      :disabled="props.disabled"
      :aria-invalid="props.variantIsError || undefined"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-labelledby="triggerLabelledBy"
      :aria-activedescendant="activeDescendant"
      @click="toggleDropdown"
      @keydown="handleTriggerKeydown"
      @animationend.self="isShaking = false"
    >
      <div
        class="flex w-full items-center gap-2"
        :class="{ 'justify-between': props.variant !== 'none' }"
      >
        <span
          v-if="hasSelected"
          class="truncate"
        >
          <slot
            name="selected"
            :selected="selected"
          >
            {{ getSelectedLabel }}
          </slot>
        </span>
        <span
          v-else
          class="truncate text-(--text-secondary)"
        >
          {{ placeholder }}
        </span>

        <IconChevronDown
          :width="10"
          :height="6"
          class="shrink-0 text-(--text-secondary) transition-transform duration-200"
          :class="{ 'rotate-180': isOpen, 'ml-auto': props.variant !== 'none' }"
        />
      </div>
    </button>
  </div>

  <OverlayTeleport>
    <Transition
      name="select-dropdown"
      @after-enter="focusSearchAfterEnter"
    >
      <div
        v-if="isOpen && !props.disabled"
        ref="dropdown"
        class="select-dropdown"
        :style="dropdownStyle"
      >
        <div
          v-if="props.searchable"
          class="relative shrink-0"
        >
          <!-- Не v-model: он ждёт конца IME-композиции, и на Android значение стоит, пока открыта клавиатура. -->
          <input
            ref="searchInput"
            :value="search"
            type="text"
            inputmode="search"
            role="combobox"
            autocomplete="off"
            aria-label="Поиск по списку"
            aria-autocomplete="list"
            :aria-expanded="isOpen"
            :aria-controls="listboxId"
            :aria-activedescendant="activeDescendant"
            placeholder="Поиск"
            class="h-11 w-full rounded-lg border border-(--field-border) bg-(--field-bg) pr-11 pl-3 text-base text-(--text-primary) outline-none placeholder:text-(--text-secondary) focus:border-(--accent)"
            @input="search = ($event.target as HTMLInputElement).value"
            @keydown="handleSearchKeydown"
          />
          <button
            v-if="search"
            type="button"
            tabindex="-1"
            aria-label="Очистить поиск"
            class="absolute top-1/2 right-0.5 flex size-10 -translate-y-1/2 items-center justify-center rounded-md text-(--text-secondary) hover:bg-(--surface-hover) hover:text-(--text-primary) focus-visible:outline-2 focus-visible:outline-(--accent)"
            @pointerdown.prevent
            @click="clearSearch"
            @keydown="handleSearchKeydown"
          >
            <IconCross
              :width="16"
              :height="16"
              aria-hidden="true"
            />
          </button>
        </div>
        <div
          :id="listboxId"
          ref="listbox"
          role="listbox"
          :aria-labelledby="hasLabel ? labelId : triggerId"
          :aria-multiselectable="props.multiple || undefined"
          class="scrollbar-sm min-h-0 overflow-y-auto overscroll-contain"
          :style="{ maxHeight: `${optionStride * 6 - 4}px` }"
          @scroll="containerProps.onScroll"
        >
          <div
            class="flex flex-col gap-1"
            :style="virtualized ? wrapperProps.style : undefined"
          >
            <AppSelectOption
              v-for="{ data: option, index } in visibleOptions"
              :id="optionId(index)"
              :key="getOptionValue(option) ?? `option-${index}`"
              :option="option"
              :is-active="activeOptionIndex === index"
              :is-selected="isValueSelected(getOptionValue(option))"
              :multiple="props.multiple"
              :option-classes="optionClasses"
              :aria-posinset="virtualized ? index + 1 : undefined"
              :aria-setsize="virtualized ? filteredOptions.length : undefined"
              :style="virtualized ? { height: `${optionHeight}px` } : undefined"
              @activate="setActiveOption(index, false)"
              @select="selectOption(option)"
            >
              <template #default="{ option: opt }">
                <slot
                  name="option"
                  :option="opt"
                >
                  {{ getOptionLabel(opt) }}
                </slot>
              </template>
            </AppSelectOption>
          </div>
        </div>
        <div
          v-if="filteredOptions.length === 0"
          role="status"
          class="text-(--text-secondary)"
          :class="optionClasses"
        >
          <slot name="empty">Нет доступных опций</slot>
        </div>

        <slot name="footer" />
      </div>
    </Transition>
  </OverlayTeleport>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  /* Базовые стили */
  .select-display {
    @apply flex w-full cursor-pointer items-center rounded-lg border border-(--field-border) bg-(--field-bg) text-(--text-primary) transition-[border-color,box-shadow] duration-100 outline-none focus:border-(--accent) focus-visible:ring-2 focus-visible:ring-(--accent)/25 motion-reduce:transition-none;
  }

  /* Размеры */
  .select-size-xs {
    @apply h-[28px] px-2 text-xs;
  }

  .select-size-sm {
    @apply h-[32px] px-3 text-sm;
  }

  .select-size-md {
    @apply h-[42px] px-3 text-base;
  }

  .select-size-lg {
    @apply h-[48px] px-4 text-lg;
  }

  .select-size-xl {
    @apply h-[56px] px-5 text-xl;
  }

  /* Варианты */
  .select-variant-none {
    @apply border-0 bg-transparent;
  }

  /* Состояния */
  .select-disabled {
    @apply cursor-not-allowed opacity-50;
  }

  /* Выпадающий список — позиционирование и max-height управляются JS (floating-ui). */
  .select-dropdown {
    @apply z-(--popup-z-index) flex min-w-40 flex-col gap-2 overflow-hidden rounded-xl border border-(--border) bg-(--surface-overlay) p-2 shadow-lg;
  }

  /* Размеры опций (для пустого состояния) */
  .select-option-size-xs {
    @apply text-xs;
    padding: 4px 12px;
  }

  .select-option-size-sm {
    @apply text-sm;
    padding: 8px 16px;
  }

  .select-option-size-md {
    @apply text-base;
    padding: 8px;
  }

  .select-option-size-lg {
    @apply text-lg;
    padding: 12px 24px;
  }

  .select-option-size-xl {
    @apply text-xl;
    padding: 16px 32px;
  }

  /* Анимации для выпадающего списка */
  .select-dropdown-enter-active,
  .select-dropdown-leave-active {
    @apply transition-[opacity,transform] duration-100 ease-out motion-reduce:transition-none;
  }

  .select-dropdown-enter-from,
  .select-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
  }

  .select-dropdown-enter-to,
  .select-dropdown-leave-from {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
